import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

// =============================================================================
// Types
// =============================================================================

export type BatchCallStatus =
  | 'draft'
  | 'scheduled'
  | 'in_progress'
  | 'retry_scheduled'
  | 'retry_in_progress'
  | 'completed'
  | 'cancelled'

export type BatchCallTiming = 'immediate' | 'scheduled'
export type NoAnswerAction = 'none' | 'sms_client'

/** Phone entry — string brut ou objet enrichi (nom, métadonnées). */
export type BatchPhoneEntry = string | Record<string, string>

export interface BatchCall {
  id: number
  name: string
  ai_agent: number
  ai_agent_name: string
  phone_number: number
  phone_number_display: string | null
  phone_numbers: BatchPhoneEntry[]
  context_message: string
  timing: BatchCallTiming
  timing_display?: string
  scheduled_at: string | null
  retry_enabled: boolean
  retry_at: string | null
  call_delay: number
  skip_if_no_appointment: boolean
  no_answer_action: NoAnswerAction
  no_answer_action_display?: string
  no_answer_sms_template: string
  status: BatchCallStatus
  status_display: string
  total_calls: number
  completed_calls: number
  answered_calls: number
  no_answer_calls: number
  failed_calls: number
  skipped_calls?: number
  success_rate: number
  estimated_duration?: number
  created_at: string
  updated_at: string
  started_at?: string | null
  completed_at?: string | null
}

interface PaginatedResponse<T> {
  results?: T[]
  count?: number
}

export interface AICallLogDetail {
  id: number
  transcript: string | null
  conversation_summary: string | null
  audio_url: string | null
  status: string
  duration: number | null
}

export interface BatchCallLog {
  id: number
  batch_call: number
  phone_number: string
  call_status: string
  call_status_display?: string
  call_sid?: string | null
  duration?: number | null
  ai_call_log?: number | null
  /** Détail enrichi de l'AI Call Log (transcript, summary, audio) — sérializé
   *  par AICallLogLightSerializer côté backend. */
  ai_call_log_detail?: AICallLogDetail | null
  attempt_number: number
  created_at: string
  scheduled_at?: string | null
  completed_at?: string | null
}

// =============================================================================
// Queries
// =============================================================================

export function useBatchCalls() {
  return useQuery({
    queryKey: ['batch-calls'],
    queryFn: async () => {
      const { data } = await api.get<BatchCall[] | PaginatedResponse<BatchCall>>(
        '/api/batch-calls/',
      )
      if (Array.isArray(data)) return data
      return data.results ?? []
    },
    staleTime: 15_000,
  })
}

export function useBatchCallDetail(id: number | null) {
  return useQuery({
    queryKey: ['batch-call', id],
    queryFn: async () => {
      const { data } = await api.get<BatchCall>(`/api/batch-calls/${id}/`)
      return data
    },
    enabled: !!id,
  })
}

export function useBatchCallLogs(id: number | null) {
  return useQuery({
    queryKey: ['batch-call-logs', id],
    queryFn: async () => {
      const { data } = await api.get<BatchCallLog[]>(
        `/api/batch-calls/${id}/logs/`,
      )
      return data
    },
    enabled: !!id,
    staleTime: 10_000,
  })
}

// =============================================================================
// Mutations
// =============================================================================

export interface CreateBatchCallInput {
  name: string
  ai_agent: number
  phone_number: number
  phone_numbers: BatchPhoneEntry[]
  context_message: string
  timing: BatchCallTiming
  scheduled_at?: string | null
  scheduled_at_timezone?: string | null
  retry_enabled: boolean
  retry_at?: string | null
  retry_at_timezone?: string | null
  call_delay: number
  skip_if_no_appointment: boolean
  no_answer_action: NoAnswerAction
  no_answer_sms_template: string
}

export interface CreateBatchCallResponse extends BatchCall {
  dnc_removed_count?: number
}

export function useCreateBatchCall() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateBatchCallInput) => {
      const { data } = await api.post<CreateBatchCallResponse>(
        '/api/batch-calls/',
        input,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batch-calls'] })
    },
  })
}

export function useUpdateBatchCall(id: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (patch: Partial<CreateBatchCallInput>) => {
      const { data } = await api.patch<BatchCall>(
        `/api/batch-calls/${id}/`,
        patch,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batch-calls'] })
      queryClient.invalidateQueries({ queryKey: ['batch-call', id] })
    },
  })
}

export function useDeleteBatchCall() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/batch-calls/${id}/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batch-calls'] })
    },
  })
}

export function useCancelBatchCall() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.post(`/api/batch-calls/${id}/cancel/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batch-calls'] })
    },
  })
}

export function useDuplicateBatchCall() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<BatchCall>(
        `/api/batch-calls/${id}/duplicate/`,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batch-calls'] })
    },
  })
}

export function useRetryFailedBatchCall() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<BatchCall>(
        `/api/batch-calls/${id}/retry_failed/`,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batch-calls'] })
    },
  })
}

export type BatchReportPeriod = 'current_month' | 'last_month' | 'last_3_months'

export function useGenerateBatchReport() {
  return useMutation({
    mutationFn: async (period: BatchReportPeriod) => {
      const { data } = await api.post<{ message: string }>(
        '/api/batch-calls/generate_report/',
        { period },
      )
      return data
    },
  })
}
